/*
 *  KAN_Network.js
 *  Project: Kanoogi Library System
 *
 *  Created by Chris Taylor on 07/04/17
 *  Copyright © 2017-2026 Kanoogi Inc. | All Rights Reserved
 *
 */

"use strict";

import { DecodeByteText } from './KAN_Binary.js';

/* ---------------------------------------------------------------- */

// DescribeWireBinary() - Identify the framed game packets carried by one
// WebSocket message without decoding their payloads. The outer frame is
// [Code4C, uint32 byte length], and a BIN0 payload starts with its own Code4C.
// This is exported so the diagnostic stays testable; Receive() remains the
// authority that validates and dispatches the actual packet.
export function DescribeWireBinary(packet)
	{
	try
		{
		if (!(packet instanceof ArrayBuffer)) return {codes:[],bytes:0,complete:false};
		const bytes=new Uint8Array(packet),view=new DataView(packet),codes=[];
		const codeAt=offset=>String.fromCharCode(bytes[offset],bytes[offset+1],bytes[offset+2],bytes[offset+3]);
		let offset=0;
		while (offset+8<=bytes.length)
			{
			const outer=codeAt(offset),size=view.getUint32(offset+4);
			if (size<8 || offset+size>bytes.length) return {codes:codes,bytes:bytes.length,complete:false};
			codes.push(outer==='BIN0' && size>=12?codeAt(offset+8):outer);
			offset+=size;
			}
		return {codes:codes,bytes:bytes.length,complete:offset===bytes.length};
		}
	catch (err) { return {codes:[],bytes:packet&&packet.byteLength||0,complete:false}; }
	}

/* ---------------------------------------------------------------- */

// KAN_Network() - Initializes the Kanoogi Communications Networking Module

export function KAN_Network()
	{
	let mainwebsocket=null;
	let defaultcallback=null;
	let binarycallback=null;
	const pendingbinary=[];											// Buffers binary packets that arrive before SetBinaryCallback is registered.  Drained on registration in arrival order.
	const pendingjson=[];											// Same idea for JSON broadcasts: buffer until SetDefaultCallback registers.
	let dconcallback=null;

	// In-page reconnect (opt-in).  With no handler an unexpected close leaves the
	// socket null and the next Send() reloads the page - right for the boot screens
	// and every page that is not the game.  The game arms a handler once it is
	// running (GSMProx/src/js/ISE_Reconnect.js): the close is handed to it, Send()
	// drops quietly while it reconnects, and Reconnect() reopens the saved URI.
	let reconnecthandler=null;
	let socketuri=null;												// The URI of the last OpenWebSocket(), for Reconnect()
	let closedonpurpose=false;										// Close() was called: nothing reconnects, and Send() must not reload either
	let droplogged=false;											// One "dropping sends" line per outage, not one per send
	let callbackid=[];												// An object which hold objects for dispatching messages

	let stats={totalbytes:0};										// Total bytes downloaded since client was started
	this.stats=stats;

	// wiretap - an OPTIONAL observer of socket traffic, null unless something
	// arms it. Deliberately a slot rather than an import: this file is a shared
	// Kanlib bundled into eight public website pages (KanoogiMain signin/home/
	// create/change/finish/joinbeta/userdata and KanoogiWeb endgame) as well as
	// the game client, and a game-only diagnostic must not ship in the signin
	// bundle. Armed by ISE_Main via SetWireTap; everywhere else this stays null
	// and the taps below cost one comparison.
	let wiretap=null;
	const streamCodes=new Set(['GTCK','SUUP','SUHP','SUBS','UVIS','SHIV','SUOG','SURV','SMOV','UIMP','SHIP','MVLS','HDMG','APRJ']);
	let streamCounts=new Map(),streamBytes=0,streamStarted=0;
	function FlushWireStream()
		{
		if (!wiretap || !streamCounts.size) return;
		const detail=Array.from(streamCounts).map(([code,count])=>`${code} ×${count}`).join(' · ');
		wiretap('net','◀ gameplay state stream',`${detail} · ${Math.round(streamBytes/1024)} KiB · assets are not reloaded`);
		streamCounts=new Map(); streamBytes=0; streamStarted=0;
		}
	function TapIncomingBinary(packet)
		{
		const frame=DescribeWireBinary(packet);
		if (frame.codes.length && frame.complete && frame.codes.every(code=>streamCodes.has(code)))
			{
			const now=(typeof performance!=='undefined'&&performance.now)?performance.now():Date.now();
			if (!streamStarted) streamStarted=now;
			for (const code of frame.codes) streamCounts.set(code,(streamCounts.get(code)||0)+1);
			streamBytes+=frame.bytes;
			if (now-streamStarted>=1000) FlushWireStream();
			return;
			}
		FlushWireStream();
		const label=frame.codes.length?frame.codes.join(' + '):'binary';
		wiretap('net',`◀ ${label}`,`${frame.bytes} bytes${frame.complete?'':' (partial frame)'}`);
		}
	this.SetWireTap=function(fn)
		{
		FlushWireStream();
		wiretap=(typeof fn==='function')?fn:null;
		streamCounts=new Map(); streamBytes=0; streamStarted=0;
		};

	let playbackmode=false;											// Playback viewer: there is no live WebSocket. Send() becomes a read-only no-op; every outbound
	let playbackcmdcb=null;											// command is forwarded to this hook so the IGSE layer can stand in for the server (start the feed
																	// on @GSTRT, echo the engine ping, ...). Recorded wire frames are fed straight into Receive().

	const DebugOnly=console.error;

/* ---------------------------------------------------------------- */

// WebSocketClient() - This is the future, will change old system over to this
// NOTE: Still unused.  The game's live reconnect is OpenWebSocket() + Reconnect() below, driven by
// GSMProx/src/js/ISE_Reconnect.js: a game socket owes an auth frame and a resync on every open, and a
// late event from a replaced socket must be ignored, neither of which this class does.

class WebSocketClient
	{
	constructor(wsUri, receiveCallback, dconCallback)
		{
		this.wsUri = wsUri;
		this.webSocket = null;
		this.receiveCallback = receiveCallback; // Callback for incoming messages
		this.dconCallback = dconCallback; // Callback for disconnection
		this.reconnectAttempts = 0;
		this.maxReconnectAttempts = 10; // Max attempts before giving up
		this.reconnectInterval = 3000; // Time in ms before next reconnect attempt
		this.isConnected = false; // Internal flag to track connection status
		this.shouldReconnect = true; // Flag to control auto-reconnection
		}

	// Connects to the WebSocket server.
	// Returns a Promise that resolves when the connection is open, or rejects on error.

	async connect()
		{
		if (this.webSocket && this.webSocket.readyState === WebSocket.OPEN)
			{
			console.debug('WebSocketClient: Already connected.');
			return Promise.resolve(); // Already connected, resolve immediately
			}

		if (this.webSocket && (this.webSocket.readyState === WebSocket.CONNECTING || this.webSocket.readyState === WebSocket.CLOSING))
			{
			console.debug('WebSocketClient():Connection in progress or closing, waiting...');
			// In a real application, you might want to wait for current state to resolve
			// For now, we'll just prevent new connection attempts.
			return Promise.reject(new Error('WebSocket connection already in progress or closing.'));
			}

		return new Promise((resolve, reject)=>
			{
			console.debug(`WebSocketClient():Attempting to connect to ${this.wsUri}...`);
			try
				{
				this.webSocket = new WebSocket(this.wsUri);
				this.webSocket.binaryType = "arraybuffer";

				this.webSocket.onopen=(event)=>
					{
					console.debug('WebSocketClient():Connected.');
					this.isConnected = true;
					this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
					resolve(event); // Resolve the promise on successful connection
					};

				this.webSocket.onmessage=(event)=>
					{
					if (this.receiveCallback)
						this.receiveCallback(event.data);
					};

				this.webSocket.onclose=(event)=>
					{
					this.isConnected = false;
					console.debug('WebSocketClient():Disconnected. Code:', event.code, 'Reason:', event.reason);

					if (this.dconCallback)
						this.dconCallback(event);

					if (this.shouldReconnect && event.code !== 1000 && event.code !== 1001)	// 1000 = Normal Closure, 1001 = Going Away
						this.handleReconnect();
					else
						reject(new Error('WebSocket disconnected unexpectedly.')); // Reject if not reconnecting
			 		};

				this.webSocket.onerror = (error) =>
					{
					console.error('WebSocketClient():Error:', error);
					this.isConnected = false;

					reject(error);					// Reject the promise if an error occurs during connection
					// The 'onclose' event will usually follow an 'onerror' event,
					// so reconnection logic is typically handled there.
					};
				}
			catch (err)
				{
				console.error('WebSocketClient():Connection attempt failed:',err);
				reject(err); // Reject the promise if an error occurs before even connecting
				}
			});
		}

	/**
	 * Sends data over the WebSocket connection.
	 * @param {any} data The data to send.
	 */
	send(data)
		{
		if (this.webSocket && this.webSocket.readyState === WebSocket.OPEN)
			this.webSocket.send(data);
 		else	// In a real app, you might queue messages or throw an error
			console.warn('WebSocketClient():Cannot send data, connection not open.', data);
		}

	/**
	 * Closes the WebSocket connection gracefully.
	 * Prevents automatic reconnection.
	 */
	disconnect()
		{
		if (this.webSocket) {
			this.shouldReconnect = false; // Prevent auto-reconnection
			console.debug('WebSocketClient: Closing connection gracefully.');
			this.webSocket.close(1000, "Client initiated disconnect"); // 1000 is normal closure
			this.webSocket = null;
		} else {
			console.debug('WebSocketClient: No active connection to disconnect.');
		}
	}

	/**
	 * Handles the reconnection logic.
	 */
	handleReconnect()
		{
		if (this.reconnectAttempts < this.maxReconnectAttempts) {
			this.reconnectAttempts++;
			console.debug(`WebSocketClient: Attempting reconnect #${this.reconnectAttempts} in ${this.reconnectInterval / 1000} seconds...`);
			setTimeout(() => this.connect(), this.reconnectInterval);
		} else {
			console.error('WebSocketClient: Max reconnect attempts reached. Giving up.');
			// Optionally, notify the user or perform other fallback actions here
		}
	}
}
/* ---------------------------------------------------------------- */

	// OpenWebSocket() - This is the main connection to the GSM, which in turn will connect the client to the GSI

	function OpenWebSocket(wsUri, callback)
		{
		try
			{
			if (mainwebsocket && mainwebsocket.readyState==1)
				mainwebsocket.close();

			console.debug(`OpenWebSocket():Connecting Websocket:${wsUri}`);

			socketuri=wsUri;
			closedonpurpose=false;
			remainder="";											// A frame half-received on the previous socket must never be glued to
			binremain=null;											// the front of this one's first frame

			const ws=new WebSocket(wsUri);							// Create the WebSocket Object and connect to the specified server
			mainwebsocket=ws;
			ws.binaryType="arraybuffer";							// We are explicitly creating a binary connection to the GSM here

			// Every handler first checks that its socket is still THE socket.  A replaced
			// one (reconnected over, or dropped as stalled) can still deliver a late close
			// or message, and that close used to null out whatever socket was current.

			ws.onopen=function(evt)
				{
				if (ws!==mainwebsocket) return;
				droplogged=false;
				if (callback)
					callback(evt);
				};

			ws.onclose=function(evt)
				{
				if (ws!==mainwebsocket) return;
				console.debug(`OpenWebSocket():WebSocket Disconnected, code:${evt&&evt.code} reason:${evt&&evt.reason}`);
				mainwebsocket=null;
				if (dconcallback)
					dconcallback(evt);								// Just for shiggles, send the event info
				if (reconnecthandler && !closedonpurpose)
					reconnecthandler(evt);
				};

			ws.onmessage=function(evt)
				{
				if (ws!==mainwebsocket) return;
				// Boot narration tap. ONE place, so no message type can be added
				// later and silently escape the trace. Binary frames are
				// SUMMARISED by size, never decoded or dumped: a world-state blob
				// is tens of KB and printing it would drown the useful lines.
				if (wiretap)
					{
					try
						{
						const d=evt.data;
						if (typeof d==='string') wiretap('net','◀ '+d.slice(0,120),'');
						else if (d instanceof ArrayBuffer) TapIncomingBinary(d);
						else wiretap('net','◀ binary',`${d&&(d.byteLength||d.size)||0} bytes`);
						}
					catch (err) { /* instrumentation must never break the socket */ }
					}
				Receive(evt.data);									// Process incoming data
				};

			ws.onerror=function(evt)
				{
				if (ws!==mainwebsocket) return;
				console.error(`OpenWebSocket():ERROR:${evt.data}`);
				};
			}
		catch (err)
			{
			console.error(`OpenWebSocket():ERROR:${err}`);
			}
		}
this.OpenWebSocket=OpenWebSocket;

	// SetReconnectHandler() - Arm (fn) or disarm (null) the in-page reconnect.  See reconnecthandler above.
	this.SetReconnectHandler=function(fn)
		{
		reconnecthandler=(typeof fn==='function')?fn:null;
		};

	// Reconnect() - Open a fresh socket on the URI of the last OpenWebSocket(); callback runs on its open.
	this.Reconnect=function(callback)
		{
		if (!socketuri) return(false);
		OpenWebSocket(socketuri,callback);
		return(true);
		};

	// Drop() - Abandon the current socket as LOST: the page decided the connection is dead (an engine
	// that stopped answering behind a socket the browser still calls open).  The socket is detached
	// before it is closed, so its own close - which a dead network may deliver much later, or never -
	// is ignored, and the loss is reported now, to the same callbacks a real close reaches.
	this.Drop=function(reason)
		{
		const ws=mainwebsocket;
		if (!ws) return(false);
		mainwebsocket=null;
		try { ws.close(); } catch (err) { /* already closing */ }
		const evt={code:4000,reason:String(reason||'dropped'),wasClean:false};
		if (dconcallback) dconcallback(evt);
		if (reconnecthandler && !closedonpurpose) reconnecthandler(evt);
		return(true);
		};

	// Close() - Close the socket on purpose (the page is leaving, or a gate has stopped the game).
	// Nothing reconnects, and later sends drop instead of reloading.
	this.Close=function()
		{
		closedonpurpose=true;
		const ws=mainwebsocket;
		mainwebsocket=null;
		if (ws) { try { ws.close(1000); } catch (err) { /* already closing */ } }
		};
/* ---------------------------------------------------------------- */

	// ParseJSON() - Wraps the JSON Parser in a try catch so that the JSON.parse() doesn't crash the client
	// NOTE: If I catch it, I might be able to recover, but probably just need to reload the client and log the error
	// so that I can figure out why this data was borked

	function ParseJSON(packet)
		{
		try {
			return(JSON.parse(packet));
			}
		catch (err)
			{
			console.error(`KAN_Network:ParseJSON():ERROR Parsing JSON:${err}`);
			console.error(`KAN_Network:ParseJSON():ERROR:Cannot parse JSON.parse():${packet}`);
			}
		return(null);
		}
/* ---------------------------------------------------------------- */

	// DispatchJSON() - Sends the packet to the right place

	function DispatchJSON(json)
		{
		try {
			if (json.id && callbackid[json.id])						// If there's an ID and it's registered, call it
				callbackid[json.id](json);
			else
			if (defaultcallback)
				defaultcallback(json);
			else
				// No id-routed callback AND no default yet (typically the
				// gap between WS auth and the brain registering its
				// MessageDispatch).  Hold the packet for replay - the
				// alternative is silently losing early-game broadcasts.
				pendingjson.push(json);
			}
		catch (err)
			{
			console.error(`KAN_Network:DispatchJSON():ERROR:${err}`);
			console.error(`KAN_Network:DispatchJSON():ERROR:No callbackid:${json}`);
			}
		}
/* ---------------------------------------------------------------- */

	// DispatchBinary() - Sends the packet to the right place

	function DispatchBinary(bindata)
		{
		if (binarycallback)
			{
			try	{
				binarycallback(bindata);
				}
			catch (err)
				{
				console.error(`KAN_Network:DispatchBinary():ERROR:${err}`);
				}
			}
		else
			{
			// No handler yet (typically during the gap between WS auth and
			// the brain registering its binary dispatcher).  Hold onto the
			// packet so it doesn't get dropped; SetBinaryCallback drains
			// the queue in arrival order.
			pendingbinary.push(bindata);
			}
		}
/* ---------------------------------------------------------------- */

// The TCP stream is carried by binary WebSocket messages; a message can contain
// several frames or only part of one. Complete frames use bounded, immutable views.
// An incomplete frame owns its storage: first an 8-byte header, then its declared
// size. Each arriving payload byte is copied at most once, never a growing tail.
	let remainder="";											// Legacy text-message reassembly
	let binremain=null;
	const MAX_FRAME_BYTES=1000000;

	function FrameSize(view)
		{
		const size=view.getUint32(4);
		if (size<8 || size>MAX_FRAME_BYTES || (view.getUint32(0)===0x42494e30 && size<16))
			{
			console.error(`Net:ERROR:Invalid frame size ${size}`);
			return(0);
			}
		return(size);
		}

	function DispatchFrame(view)
		{
		stats.totalbytes+=view.byteLength;
		switch (view.getUint32(0))
			{
			case 0x42494e30: // BIN0, followed by inner Code4C + GTick
			DispatchBinary(new DataView(view.buffer,view.byteOffset+8,view.byteLength-8));
			break;
			case 0x54455854: // TEXT
			DispatchJSON(ParseJSON(DecodeByteText(view,8)));
			break;
			}
		}

	function ReceiveBinary(packet)
		{
		const bytes=new Uint8Array(packet);
		let offset=0;
		while (offset<bytes.length)
			{
			if (binremain)
				{
				const partial=binremain;
				const count=Math.min(partial.bytes.length-partial.used,bytes.length-offset);
				partial.bytes.set(bytes.subarray(offset,offset+count),partial.used);
				partial.used+=count;
				offset+=count;
				if (partial.used<partial.bytes.length) return;
				if (!partial.size) // The split header is now complete; validate before allocating.
					{
					partial.size=FrameSize(new DataView(partial.bytes.buffer));
					if (!partial.size) { binremain=null; return; }
					if (partial.size>8)
						{
						const whole=new Uint8Array(partial.size);
						whole.set(partial.bytes);
						partial.bytes=whole;
						continue;
						}
					}
				binremain=null; // Never reuse storage after dispatch: a future-tick queue may hold the view.
				DispatchFrame(new DataView(partial.bytes.buffer));
				continue;
				}

			const remaining=bytes.length-offset;
			const view=new DataView(packet,offset,remaining);
			const size=remaining>=8?FrameSize(view):0;
			if (remaining>=8 && !size) return;
			if (remaining<8 || remaining<size)
				{
				const held=new Uint8Array(size||8);
				held.set(bytes.subarray(offset));
				binremain={bytes:held,used:remaining,size:size};
				return;
				}
			DispatchFrame(new DataView(packet,offset,size));
			offset+=size;
			}
		}

	function Receive(packet)
		{
		let openbrace=0,start=0,instring=false,escaped=false;
		if (packet instanceof ArrayBuffer)
			ReceiveBinary(packet);
		else	// Legacy plain-text messages may contain concatenated or incomplete JSON objects.
			{
			packet=remainder+packet;
			if (!packet.length) return;

			if (packet.charAt(0)!='{')
				{
				console.error(`Net:ERROR:JSON packet needs to start with an opening brace '{'`);
				console.error(`Packet:${packet}`);
				return;
				}

			for (let i=0; i<packet.length; i++)						// This code processes the packet to make sure only complete JSON packets are accepted
				{													// and if a partial comes in, waits for the rest of it
				let char=packet.charAt(i);

				// Structural braces count only outside strings. Consume escapes one
				// character at a time so odd/even runs of backslashes handle quotes correctly.
				if (instring)
					{
					if (escaped) escaped=false;
					else if (char=='\\') escaped=true;
					else if (char=='"') instring=false;
					}
				else if (char=='"') instring=true;
				else if (char=='{')
					openbrace++;
				else
				if (char=='}')
					openbrace--;

				if (!openbrace && !instring)
					{
					if (packet.charAt(start)!='{')
						{
						console.error(`Net:ERROR:JSON packet should start with {`);
						start=packet.length;
						break;
						}
					else
						{
						DispatchJSON(ParseJSON(packet.slice(start,i+1)));
						stats.totalbytes+=i+1-start;
						start=i+1;
						}
					}
				}

			if (packet.length==start)								// Entire packet was consumed?
				remainder="";										// Faster way to slice when it's the whole string
			else
				remainder=packet.slice(start);						// Slice out everything from start onward and keep for next time
			}

		}
/* ---------------------------------------------------------------- */

	// Send() - Sends the message as UTF-8 text to the server over a websocket, could be JSON encoded or not

	function Send(message, id, callback)
		{
		if (playbackmode)											// Playback viewer: swallow all outbound (the recording is read-only), never reload. Forward
			{														// every command to the IGSE hook (it starts the feed on @GSTRT, fakes the engine ping reply,
			if (playbackcmdcb && typeof message==='string')			// ...) and resolve any awaited WebsocketSend() with a benign stub so boot can't hang.
				{ try { playbackcmdcb(message); } catch (err) { console.error(`Send():playback command hook:${err}`); } }
			if (callback) callback({});
			return true;
			}

		if (mainwebsocket)											// Are we connected?
			{
			switch(mainwebsocket.readyState)
				{
				case 0:
				console.error(`Send():Can't send because socket is still connecting: ${message}`);
				return false;

				case 1:
				callbackid[id]=callback;
				if (wiretap)
					{
					try { wiretap('net','▶ '+String(message).slice(0,120),''); }
					catch (err) { /* instrumentation must never break the socket */ }
					}
				mainwebsocket.send(message);

//				console.debug("Send():Message:",message);
				return true;

				case 2:
				console.error(`Send():Can't send because socket is closing: ${message}`);
				return false;

				case 3:
				console.error(`Send():Can't send because socket is closed: ${message}`);
				return false;
				}
			return false;
			}
		else
		if (reconnecthandler || closedonpurpose)					// Reconnecting in page (or closed on purpose): this send is lost, the page is not
			{
			if (!droplogged) console.warn(`Send():WebSocket not connected - dropping sends until it reconnects: ${message}`);
			droplogged=true;
			return false;
			}
		else
			{
			console.error(`Send():WebSocket Connection Lost - Reloading page`);
			location.reload(true);									// No reconnect armed (boot screens, website pages): reload
			return false;
			}
		}
	this.Send=Send;
	this.IsConnected=function() { return !!mainwebsocket && mainwebsocket.readyState===1; };

	// Receive() is the single entry point every inbound WS message takes (OpenWebSocket's
	// onmessage calls it). Exposing it lets the Playback viewer feed recorded wire frames
	// straight in — byte-identical to a live broadcast, so the brain renders them the same.
	this.Receive=Receive;

	// SetPlaybackMode() - Enter read-only playback mode (no live socket). cmdcb(message) receives
	// every outbound command the brain would have sent, so the IGSE layer can stand in for the
	// server: start the recorded feed on @GSTRT, echo @GPING back as a pingbackend, and so on.
	this.SetPlaybackMode=function(cmdcb)
		{
		playbackmode=true;
		playbackcmdcb=cmdcb||null;
		};
/* ---------------------------------------------------------------- */

	// WebsocketSend() - This sends an object that first needs to be converted to JSON

	function WebsocketSend(object, id)
		{
		const jsontext=JSON.stringify(object);						// Convert object to json

		return new Promise((resolve)=>
			{
			Send(jsontext,id,resolve);
			});
		}
	this.WebsocketSend=WebsocketSend;
/* ---------------------------------------------------------------- */

	// FetchText() - Request a regular text file/blob from the server
	// NOTE: This has a little extra verification to make sure the data returned is text/html

	this.FetchText=async function(url, data)
		{
		try {
			const method=data?'POST':'GET';
			const options={method:method,headers:{'Content-Type':'text/html'}};
			if (data) options.body=data;

			const resp=await fetch(url,options);
			if (!resp.ok) throw new Error(`HTTP Error: ${resp.status}`);

			const contentType=resp.headers.get('content-type');
			if (contentType && (contentType.includes('text/html') || contentType.includes('text/plain') || contentType.includes('application/json')))
				return(await resp.text());

			DebugOnly('FetchText():Data returned from server did not have ContentType header set correctly');
			return('error - invalid content type');
			}
		catch (err)
			{
			DebugOnly(`FetchText():ERROR making request:${url} ERROR:${err}`);
			throw(err);
			}
		}
/* ---------------------------------------------------------------- */

	// FetchObj() - Sends converted object as JSON and then converts JSON back into object
	// NOTE: This has a little extra verification to make sure the data returned is application/json

	this.FetchObj=async function(url, obj)
		{
		try {
			const response=await fetch(url,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(obj)});
			if (!response.ok) throw new Error(`HTTP error! status:${response.status}`);

			const contentType=response.headers.get('content-type');
			if (contentType && contentType.includes('application/json'))
				return(await response.json());							// Parse JSON back into object

			DebugOnly('FetchObj():Data returned from server did not have ContentType header set to:application/json');
			return('error - not application/json');
			}
		catch (err)
			{
			DebugOnly(`FetchObj():ERROR making request:${url} :${err}`);
			throw(err);
			}
		};
/* ---------------------------------------------------------------- */

	// FetchJSON() - Request JSON from the server via GET

	this.FetchJSON=async function(url)
		{
		try {
			const headers={};
			if (url.startsWith('/'))
				{
				const jwt=localStorage.getItem('token');
				if (jwt) headers['Authorization']=`Bearer ${jwt}`;
				}

			const resp=await fetch(url,{method:'GET',headers:headers});
			if (!resp.ok) throw new Error(`HTTP error! status:${resp.status}`);

			return(await resp.json());
			}
		catch (err)
			{
			DebugOnly(`FetchJSON():ERROR making request:${url} :${err}`);
			throw(err);
			}
		}
/* ---------------------------------------------------------------- */

	// SendJSON() - Send JSON to the server via POST

	this.SendJSON=async function(url, data)
		{
		try {
			const headers={'Content-Type':'application/json'};
			if (url.startsWith('/'))
				{
				const jwt=localStorage.getItem('token');
				if (jwt) headers['Authorization']=`Bearer ${jwt}`;
				}

			const resp=await fetch(url,{method:'POST',headers:headers,body:JSON.stringify(data)});

			// A 429 has to survive as more than "HTTP error". The server sends
			// Retry-After, and the composer's cooldown counts down from exactly that
			// value — so the UI never keeps its own copy of the rate-limit numbers and
			// cannot drift when they are tuned server-side. Surfaced two ways: on the
			// thrown Error (for a caller that wants it) and via the OnRateLimited hook
			// (so the ~8 /kancom/comment call sites need no individual handling).
			if (resp.status===429)
				{
				let retryAfter=parseInt(resp.headers.get('Retry-After')||'',10);
				let payload=null;
				try { payload=await resp.clone().json(); } catch (e) { payload=null; }
				if (!Number.isFinite(retryAfter) || retryAfter<1)
					retryAfter=(payload && Number(payload.retryAfterSeconds))||1;
				if (typeof this.OnRateLimited==='function')
					{ try { this.OnRateLimited(retryAfter,payload); } catch (e) {} }
				const err=new Error('rate limited');
				err.status=429;
				err.retryAfterSeconds=retryAfter;
				err.payload=payload;
				throw(err);
				}

			if (!resp.ok) throw new Error(`HTTP error! status:${resp.status}`);

			return(await resp.json());
			}
		catch (err)
			{
			DebugOnly(`SendJSON():ERROR making request:${url} :${err}`);
			throw(err);
			}
		}
/* ---------------------------------------------------------------- */

	// SendBinary() - POST a Blob/ArrayBuffer payload (e.g. a recorded video). Mirrors
	// SendJSON's JWT-injection: same-origin requests carry the bearer token from
	// localStorage. Caller picks the Content-Type (e.g. 'video/webm') so the server
	// can route via express.raw({type:...}).

	this.SendBinary=async function(url, blob, contentType)
		{
		try {
			const headers={'Content-Type':contentType||'application/octet-stream'};
			if (url.startsWith('/'))
				{
				const jwt=localStorage.getItem('token');
				if (jwt) headers['Authorization']=`Bearer ${jwt}`;
				}

			const resp=await fetch(url,{method:'POST',headers:headers,body:blob});
			if (!resp.ok) throw new Error(`HTTP error! status:${resp.status}`);

			return(await resp.json());
			}
		catch (err)
			{
			DebugOnly(`SendBinary():ERROR making request:${url} :${err}`);
			throw(err);
			}
		}
/* ---------------------------------------------------------------- */

	// SetDefaultCallback() - Sets the default callback to be used if no callback is provided

	this.SetDefaultCallback=function(callback)
		{
		defaultcallback=callback;

		// Drain JSON broadcasts that arrived before the default callback
		// was registered.  Splice up front to preserve ordering across
		// any synchronous re-entry.
		if (callback && pendingjson.length)
			{
			const drained=pendingjson.splice(0, pendingjson.length);
			for (const json of drained)
				{
				try { callback(json); }
				catch (err)
					{
					console.error(`KAN_Network:SetDefaultCallback():drain ERROR:${err}`);
					}
				}
			}
		};
/* ---------------------------------------------------------------- */

	// SetBinaryCallback() - Sets the default binary callback to be used if no callback is provided

	this.SetBinaryCallback=function(callback)
		{
		binarycallback=callback;

		// Drain anything that arrived before the callback was registered.
		// Splice from the front to preserve arrival order in case the
		// callback synchronously triggers another DispatchBinary that
		// would re-queue.
		if (callback && pendingbinary.length)
			{
			const drained=pendingbinary.splice(0, pendingbinary.length);
			for (const bindata of drained)
				{
				try { callback(bindata); }
				catch (err)
					{
					console.error(`KAN_Network:SetBinaryCallback():drain ERROR:${err}`);
					}
				}
			}
		};
/* ---------------------------------------------------------------- */

	// SetDisconnectCallback() - Sets the callback to the function when the WebSocket connection is lost

	this.SetDisconnectCallback=function(callback)
		{
		dconcallback=callback;
		};
/* ---------------------------------------------------------------- */

	// SetCallbackID() - This allows the application to set up a callback for any incoming message that matches
	// the callback ID.

	this.SetCallbackID=function(id,callback)
		{
		callbackid[id]=callback;
		}
	}
